import { useState, type ReactElement } from 'react'
import { Check, Disc } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Card } from '../components/ui/card.js'
import { Badge } from '../components/ui/badge.js'
import { ScanResultRow } from '../components/ScanResultRow.js'
import { OverrideDialog } from '../components/OverrideDialog.js'
import { SavedPicturesStrip } from '../components/SavedPicturesStrip.js'
import { useStep } from '../state/useStep.js'
import { useDemoStore, type ValveType, type DrainType, type OutletSide } from '../state/store.js'

const VALVE_OPTIONS: { label: string; value: ValveType }[] = [
  { label: 'Standard quarter-turn shutoff', value: 'quarter-turn' },
  { label: 'Gate valve', value: 'gate' },
  { label: 'Compression valve', value: 'compression' },
  { label: 'None visible', value: 'none-visible' },
]
const DRAIN_OPTIONS: { label: string; value: DrainType }[] = [
  { label: 'Standard drain hose hookup', value: 'standard-hose' },
  { label: 'Garbage disposal connection', value: 'garbage-disposal' },
  { label: 'None visible', value: 'none-visible' },
]
const SIDE_OPTIONS: { label: string; value: OutletSide }[] = [
  { label: 'Left', value: 'left' }, { label: 'Right', value: 'right' },
  { label: 'Back', value: 'back' }, { label: 'None visible', value: 'none-visible' },
]

const VALVE_LABEL: Record<ValveType, string> = {
  'quarter-turn': 'Standard quarter-turn shutoff', 'gate': 'Gate valve',
  'compression': 'Compression valve', 'none-visible': 'None visible',
}
const DRAIN_LABEL: Record<DrainType, string> = {
  'standard-hose': 'Standard drain hose hookup', 'garbage-disposal': 'Garbage disposal connection',
  'none-visible': 'None visible',
}

const Dot = (): ReactElement => <Disc className="h-4 w-4" />

export const UndersinkResultsScreen = (): ReactElement => {
  const { goto } = useStep()
  const { state, dispatch } = useDemoStore()
  const us = state.undersink
  const [editing, setEditing] = useState<'valve' | 'drain' | 'outlet' | 'loop' | null>(null)

  if (!us) return <AppShell title="Under-sink results"><p>No scan data yet.</p></AppShell>

  const loopStatus: 'ok' | 'warn' = us.drainLoopInches >= 32 ? 'ok' : 'warn'

  return (
    <AppShell
      title="Under-sink results"
      showBack onBack={() => goto('scan-undersink')}
      progress={0.65}
      footer={<Button size="xl" onClick={() => goto('qa')}>Looks Good — Next: A Few Questions</Button>}
    >
      <Badge variant="success" className="mb-4">
        <Check className="h-3 w-3" /> Under-sink scan complete
      </Badge>
      <h1 className="mb-2">Plumbing connections</h1>
      <p className="text-sm text-muted-foreground mb-4">We identified the following under your sink. Tap any row to correct a value.</p>

      <SavedPicturesStrip scan="undersink" />

      <Card className="p-5">
        <ScanResultRow icon={<Dot />} label="Water supply valve" value={VALVE_LABEL[us.valve]} status="ok" onOverride={() => setEditing('valve')} />
        <ScanResultRow icon={<Dot />} label="Drain connection"   value={DRAIN_LABEL[us.drain]} status="ok" onOverride={() => setEditing('drain')} />
        <ScanResultRow
          icon={<Dot />} label="Electrical"
          value={us.outlet.present ? `110V outlet detected (${us.outlet.side})` : 'No outlet visible'}
          status={us.outlet.present ? 'ok' : 'warn'}
          onOverride={() => setEditing('outlet')}
        />
        <ScanResultRow
          icon={<Dot />} label="Drain loop height"
          value={`~${us.drainLoopInches} inches — ${loopStatus === 'ok' ? 'meets code' : 'check measurement'}`}
          status={loopStatus}
          onOverride={() => setEditing('loop')}
        />
      </Card>

      <OverrideDialog<ValveType> open={editing === 'valve'} title="Water supply valve" options={VALVE_OPTIONS} current={us.valve}
        onSelect={v => { dispatch({ type: 'OVERRIDE_UNDERSINK', patch: { valve: v } }); setEditing(null) }}
        onClose={() => setEditing(null)} />
      <OverrideDialog<DrainType> open={editing === 'drain'} title="Drain connection" options={DRAIN_OPTIONS} current={us.drain}
        onSelect={v => { dispatch({ type: 'OVERRIDE_UNDERSINK', patch: { drain: v } }); setEditing(null) }}
        onClose={() => setEditing(null)} />
      <OverrideDialog<OutletSide> open={editing === 'outlet'} title="Outlet location" options={SIDE_OPTIONS} current={us.outlet.side}
        onSelect={v => {
          dispatch({ type: 'OVERRIDE_UNDERSINK', patch: { outlet: { present: v !== 'none-visible', side: v } } })
          setEditing(null)
        }}
        onClose={() => setEditing(null)} />
      <OverrideDialog<number> open={editing === 'loop'} title="Drain loop height (inches)"
        options={[{ label: '< 24"', value: 20 }, { label: '24–32"', value: 28 }, { label: '> 32"', value: 34 }]}
        current={us.drainLoopInches} allowFreeform freeformPlaceholder="e.g. 30"
        onSelect={v => {
          const n = typeof v === 'number' ? v : Number(v)
          if (!Number.isFinite(n)) return
          dispatch({ type: 'OVERRIDE_UNDERSINK', patch: { drainLoopInches: n } })
          setEditing(null)
        }}
        onClose={() => setEditing(null)} />
    </AppShell>
  )
}
